import React, { useState, useMemo, useRef } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FaPlus } from "react-icons/fa";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import {
  useGetOpenHomeMusicQuery,
  useCreateOpenHomeMusicMutation,
  useUpdateOpenHomeMusicMutation,
  useDeleteOpenHomeMusicMutation,
} from "apis/openHomeMusic";

// ─── Tag chips helper ────────────────────────────────────────────────────────
const TagList = ({ tags }) => {
  if (!tags?.length) return <span className="text-darkgray">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t, i) => (
        <span
          key={i}
          className="px-2 py-0.5 bg-lightgray text-darkergray rounded text-xs"
        >
          {t}
        </span>
      ))}
    </div>
  );
};

// ─── Add / Edit modal ─────────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: "",
  vibeTags: "",
  propertyTypes: "",
  energyLevel: "",
  tempo: "",
  bestTimeOfDay: "",
  season: "",
  mood: "",
  notes: "",
};

const TrackModal = ({ isOpen, onClose, track, onCreate, onUpdate, isSaving }) => {
  const isEdit = !!track;
  const [form, setForm] = useState(EMPTY_FORM);
  const [audioFile, setAudioFile] = useState(null);
  const audioInputRef = useRef();

  // Sync form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        setForm({
          title: track.title ?? "",
          vibeTags: (track.vibeTags ?? []).join(", "),
          propertyTypes: (track.propertyTypes ?? []).join(", "),
          energyLevel: track.energyLevel ?? "",
          tempo: track.tempo ?? "",
          bestTimeOfDay: (track.bestTimeOfDay ?? []).join(", "),
          season: (track.season ?? []).join(", "),
          mood: track.mood ?? "",
          notes: track.notes ?? "",
        });
      } else {
        setForm(EMPTY_FORM);
        setAudioFile(null);
      }
    }
  }, [isOpen, track, isEdit]);

  const splitTags = (str) =>
    str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      const payload = { title: form.title };
      if (form.vibeTags) payload.vibeTags = splitTags(form.vibeTags);
      if (form.propertyTypes) payload.propertyTypes = splitTags(form.propertyTypes);
      if (form.energyLevel) payload.energyLevel = form.energyLevel;
      if (form.tempo) payload.tempo = form.tempo;
      if (form.bestTimeOfDay) payload.bestTimeOfDay = splitTags(form.bestTimeOfDay);
      if (form.season) payload.season = splitTags(form.season);
      if (form.mood) payload.mood = form.mood;
      if (form.notes) payload.notes = form.notes;
      onUpdate({ id: track._id, data: payload });
    } else {
      const fd = new FormData();
      fd.append("audio", audioFile);
      fd.append("title", form.title);
      if (form.vibeTags) splitTags(form.vibeTags).forEach((t) => fd.append("vibeTags[]", t));
      if (form.propertyTypes) splitTags(form.propertyTypes).forEach((t) => fd.append("propertyTypes[]", t));
      if (form.energyLevel) fd.append("energyLevel", form.energyLevel);
      if (form.tempo) fd.append("tempo", form.tempo);
      if (form.bestTimeOfDay) splitTags(form.bestTimeOfDay).forEach((t) => fd.append("bestTimeOfDay[]", t));
      if (form.season) splitTags(form.season).forEach((t) => fd.append("season[]", t));
      if (form.mood) fd.append("mood", form.mood);
      if (form.notes) fd.append("notes", form.notes);
      onCreate(fd);
    }
  };

  const inputClass =
    "form-input border border-mediumgray rounded focus:border-darkgray";
  const selectClass =
    "form-select border border-mediumgray rounded focus:border-darkgray";
  const textareaClass =
    "form-textarea border border-mediumgray rounded focus:border-darkgray resize-none";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Track" : "Upload Track"}
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="form-label">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            className={inputClass}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        {/* Audio file — create only */}
        {!isEdit && (
          <div>
            <label className="form-label">
              Audio File <span className="text-red-500">*</span>
            </label>
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              className="block w-full text-sm text-darkergray file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-lightgray file:text-darkergray hover:file:bg-mediumgray cursor-pointer"
              onChange={(e) => setAudioFile(e.target.files[0] || null)}
              required
            />
            {audioFile && (
              <p className="mt-1 text-xs text-darkgray">{audioFile.name}</p>
            )}
          </div>
        )}

        {/* Two-column grid for compact fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">Energy Level</label>
            <select
              className={selectClass}
              value={form.energyLevel}
              onChange={(e) => setForm({ ...form, energyLevel: e.target.value })}
            >
              <option value="">Select…</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label className="form-label">Tempo</label>
            <select
              className={selectClass}
              value={form.tempo}
              onChange={(e) => setForm({ ...form, tempo: e.target.value })}
            >
              <option value="">Select…</option>
              <option value="slow">Slow</option>
              <option value="mid-tempo">Mid-tempo</option>
              <option value="upbeat">Upbeat</option>
              <option value="fast">Fast</option>
            </select>
          </div>
        </div>

        {/* Mood */}
        <div>
          <label className="form-label">Mood</label>
          <input
            className={inputClass}
            value={form.mood}
            onChange={(e) => setForm({ ...form, mood: e.target.value })}
            placeholder="e.g. relaxed, energetic"
          />
        </div>

        {/* Vibe Tags */}
        <div>
          <label className="form-label">Vibe Tags</label>
          <input
            className={inputClass}
            value={form.vibeTags}
            onChange={(e) => setForm({ ...form, vibeTags: e.target.value })}
            placeholder="Comma-separated, e.g. indie, chill"
          />
        </div>

        {/* Property Types */}
        <div>
          <label className="form-label">Property Types</label>
          <input
            className={inputClass}
            value={form.propertyTypes}
            onChange={(e) => setForm({ ...form, propertyTypes: e.target.value })}
            placeholder="Comma-separated, e.g. house, apartment"
          />
        </div>

        {/* Best Time of Day */}
        <div>
          <label className="form-label">Best Time of Day</label>
          <input
            className={inputClass}
            value={form.bestTimeOfDay}
            onChange={(e) => setForm({ ...form, bestTimeOfDay: e.target.value })}
            placeholder="Comma-separated, e.g. morning, afternoon"
          />
        </div>

        {/* Season */}
        <div>
          <label className="form-label">Season</label>
          <input
            className={inputClass}
            value={form.season}
            onChange={(e) => setForm({ ...form, season: e.target.value })}
            placeholder="Comma-separated, e.g. summer, winter"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="form-label">Notes</label>
          <textarea
            className={textareaClass}
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="outline-button" onClick={onClose}>
            Cancel
          </button>
          <Button
            type="submit"
            className="black-button"
            loading={isSaving}
          >
            {isEdit ? "Save Changes" : "Upload"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Delete confirm modal ─────────────────────────────────────────────────────
const DeleteModal = ({ isOpen, onClose, onConfirm, isDeleting }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Delete Track" className="max-w-sm">
    <p className="text-darkergray text-sm">
      Are you sure you want to delete this track? This action cannot be undone.
    </p>
    <div className="flex justify-end gap-2 pt-4">
      <button className="outline-button" onClick={onClose}>
        Cancel
      </button>
      <Button className="black-button" loading={isDeleting} onClick={onConfirm}>
        Delete
      </Button>
    </div>
  </Modal>
);

// ─── Audio player row ─────────────────────────────────────────────────────────
const AudioPlayer = ({ src }) => {
  if (!src) return <span className="text-darkgray text-xs">No audio</span>;
  return (
    <audio
      controls
      src={src}
      className="h-8 w-48"
      preload="none"
    />
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const pageSize = 10;

const OpenHomeMusic = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);

  const { data, isPending } = useGetOpenHomeMusicQuery();

  const { mutate: createTrack, isPending: isCreating } =
    useCreateOpenHomeMusicMutation();
  const { mutate: updateTrack, isPending: isUpdating } =
    useUpdateOpenHomeMusicMutation();
  const { mutate: deleteTrack, isPending: isDeleting } =
    useDeleteOpenHomeMusicMutation();

  const handleCreate = (formData) => {
    createTrack(formData, { onSuccess: () => setIsAddOpen(false) });
  };

  const handleUpdate = (payload) => {
    updateTrack(payload, { onSuccess: () => setIsEditOpen(false) });
  };

  const handleDelete = () => {
    if (!selectedTrack) return;
    deleteTrack(selectedTrack._id, { onSuccess: () => setIsDeleteOpen(false) });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIdx, currentPage) =>
          (currentPage - 1) * pageSize + rowIdx + 1,
        isFilter: false,
        isSortable: false,
        minWidth: "48px",
        maxWidth: "48px",
      },
      {
        accessorKey: "title",
        header: "Title",
        isFilter: true,
        minWidth: "160px",
      },
      {
        accessorKey: "signedUrl",
        header: "Preview",
        isFilter: false,
        isSortable: false,
        minWidth: "200px",
        cell: (row) => <AudioPlayer src={row.signedUrl} />,
      },
      {
        accessorKey: "energyLevel",
        header: "Energy",
        isFilter: true,
        cell: (row) => (
          <span className="capitalize">{row.energyLevel ?? "—"}</span>
        ),
      },
      {
        accessorKey: "tempo",
        header: "Tempo",
        isFilter: true,
        cell: (row) => (
          <span className="capitalize">{row.tempo ?? "—"}</span>
        ),
      },
      {
        accessorKey: "mood",
        header: "Mood",
        isFilter: true,
        cell: (row) => row.mood ?? "—",
      },
      {
        accessorKey: "vibeTags",
        header: "Vibe Tags",
        isFilter: false,
        isSortable: false,
        cell: (row) => <TagList tags={row.vibeTags} />,
      },
      {
        accessorKey: "propertyTypes",
        header: "Property Types",
        isFilter: false,
        isSortable: false,
        cell: (row) => <TagList tags={row.propertyTypes} />,
      },
      {
        accessorKey: "bestTimeOfDay",
        header: "Best Time",
        isFilter: false,
        isSortable: false,
        cell: (row) => <TagList tags={row.bestTimeOfDay} />,
      },
      {
        accessorKey: "season",
        header: "Season",
        isFilter: false,
        isSortable: false,
        cell: (row) => <TagList tags={row.season} />,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <button
              className="view-icon"
              title="Edit"
              onClick={() => {
                setSelectedTrack(row);
                setIsEditOpen(true);
              }}
            >
              <FiEdit2 size={16} />
            </button>
            <button
              className="view-icon"
              title="Delete"
              onClick={() => {
                setSelectedTrack(row);
                setIsDeleteOpen(true);
              }}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const UploadButton = () => (
    <button
      className="black-button flex items-center gap-2"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={14} />
      Upload Track
    </button>
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      {/* Add modal */}
      <TrackModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onCreate={handleCreate}
        isSaving={isCreating}
      />

      {/* Edit modal */}
      <TrackModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        track={selectedTrack}
        onUpdate={handleUpdate}
        isSaving={isUpdating}
      />

      {/* Delete confirm */}
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      <div className="container mx-auto">
        <Table
          title="Open Home Music"
          columns={columns}
          data={data}
          pageSize={pageSize}
          ButtonCmp={UploadButton}
          emptyText="No tracks found."
        />
      </div>
    </>
  );
};

export default OpenHomeMusic;
